import Button from 'components/button';
import OrderHeader from 'components/order-header';
import OrderProductTable from 'components/order-product-table';
import Spinner from 'components/spinner';
import {autobind} from 'core-decorators';
import {isSessionSupervisor} from 'core/utils';
import modifyOrder from 'graphql/mutations/orders/modify-cl-dealer-kits-order.gql';
import actionsQuery from 'graphql/queries/order-actions-for-categories.gql';
import orderQuery from 'graphql/queries/order.gql';
import userQuery from 'graphql/queries/user.gql';
import React, {Component} from 'react';
import {compose, graphql} from 'react-apollo';
import AttendeeData from './attendee-data';
import AttendeeForm from './attendee-form';
import ExtraData from './extra-data';
import OrderLogs from './order-logs';
import OrderStatus from './order-status';
import RouteSigns from './route-sign-form';
import styles from './styles.css';

import OrderDetailExportData from './order-detail-export';

@compose(
    graphql(userQuery, {name: 'userData'}),
    graphql(actionsQuery, {name: 'actionsData'}),
    graphql(orderQuery, {
        name: 'orderData',
        options: (ownProps) => ({
            variables: {
                id: ownProps.match.params.id,
            },
        }),
    }),
    graphql(modifyOrder, {
        props: ({mutate, ownProps}) => ({
            onSubmit: variables => mutate({variables, refetchQueries: [{query: orderQuery, variables: {id: ownProps.match.params.id}}]}),
        }),
    }),
)

@autobind
export default class OrderDetail extends Component {
    state = {
        edit: false,
        items: [],
        errors: '',
    };

    shouldShowStatus(order) {
        const categoryActions = this.props.actionsData.actionsForCategories[order.category.id];
        return ['supervisor', 'organization'].some(userType => (
            Object.keys(categoryActions[userType]).length > 0
        ));
    }

    getAvailableActions(order) {
        const {actionsData: {actionsForCategories}, userData: {user}} = this.props;
        const userType = isSessionSupervisor(user) ? 'supervisor' : 'organization';
        return (actionsForCategories[order.category.id][userType][order.status.id] || []);
    }

    setTableEdit() {
        this.setState({edit: !this.state.edit});
        this.setState({errors: ''});
    }

    handleUpdate(values) {
        const existentItem = this.state.items.findIndex(item => item.item === values.item);
        if (existentItem !== -1) {
            this.state.items[existentItem].quantity = values.quantity;
        } else {
            this.setState({
                items: [...this.state.items, values],
            });
        }
    }

    async handleSubmit() {
        try {
            const response = await this.props.onSubmit({order: this.props.orderData.order.id.toString(), items: this.state.items});
            this.setState({edit: !this.state.edit});
            this.setState({errors: ''});
        } catch (error) {
            this.setState({errors: error.message.replace('[', '').replace(']', '')});
        }
    }

    renderOrderStatusDetail(order) {
        const actions = this.getAvailableActions(order);
        if (!actions.length) return null;
        return <OrderStatus order={order} actions={actions} onStatusChange={this.props.orderData.refetch} />;
    }

    renderAttendeeForm(order) {
        if (
            order.category.id === 'loyalty_dealer_refund' &&
            order.status.id !== 'invoice_approved'
        ) {
            return (
                <AttendeeForm
                    orderId={order.id}
                    orderDataQuery={this.props.orderData}
                    vacancies={order.extraData.attendees - order.attendees.length}
                />
            );
        }
    }

    renderExtraData(order) {
        return Object.values(order.extraData || {}).some(Boolean) && (
            <ExtraData order={order} />
        );
    }

    renderOrderLog(order) {
        return this.shouldShowStatus(order) && (
            <div>
                <div className={styles.heading}>
                    <span>
                        <span className={styles.semibold}>Historial de estados</span>
                    </span>
                </div>
                <OrderLogs logs={order.log} />
            </div>
        );
    }

    renderAttendeeData(order) {
        if (order.attendees.length > 0) {
            return (
                <div>
                    <div className={styles.heading}>
                        <span>
                            <span className={styles.semibold}>Datos de los invitados</span>
                        </span>
                    </div>
                    <AttendeeData
                        attendees={order.attendees}
                        orderDataQuery={this.props.orderData}
                        orderId={order.id}
                    />
                </div>
            );
        }
    }

    renderRouteSings(order, userData) {
        if (order.category.id.includes('route')) {
            return (
                <RouteSigns order={order} userData={userData} />
            );
        }
    }

    renderShipments(order) {
        const columns = (
            order.category.id === 'merchandising'
                ? ['product', 'code', 'price', 'quantity', 'total']
                : ['product', 'code', 'quantity', 'instructions']
        );

        return order.shipments.map(shipment => (
            <div key={shipment.id}>
                {this.state.errors && <div className={styles.error}>{this.state.errors}</div>}
                <div className={styles.heading}>
                    <span>
                        <span className={styles.semibold}>ENVÍO #{shipment.index}</span>
                        {shipment.status.name}
                    </span>
                    <span>
                        <span className={styles.semibold}>TRACKING</span>
                        {shipment.trackingId || 'NO DISPONIBLE'}
                    </span>
                </div>
                {shipment.items && (
                    <div className={styles.tableContainer}>
                        <OrderProductTable
                            readOnly={!this.state.edit}
                            items={shipment.items}
                            onUpdate={this.handleUpdate}
                            hideTotal={order.total === 0}
                            columns={columns}
                        />
                        <div className={styles.editButton}>
                            {this.state.edit === false ? (
                                <Button onClick={this.setTableEdit} theme='yellow'>
                                    Editar cantidad de items
                                </Button>
                            ) : (
                                <div>
                                    <Button onClick={this.setTableEdit} theme='red' className={styles.cancelButton}>
                                        Cancelar
                                    </Button>
                                    <Button onClick={this.handleSubmit} theme='yellow'>
                                        Guardar cambios
                                    </Button>
                                </div>
                            )
                            }
                        </div>
                    </div>
                )}
            </div>
        ));
    }

    render() {
        const {orderData, userData, actionsData} = this.props;
        if (orderData.loading || userData.loading || actionsData.loading) return <Spinner />;

        const {order} = this.props.orderData;
        const extraData = this.renderExtraData(order);
        const attendeeForm = this.renderAttendeeForm(order);
        const orderStatusDetail = this.renderOrderStatusDetail(order);

        return (
            <div className={styles.root}>
                <OrderDetailExportData orderData={order} />
                <OrderHeader
                    order={order}
                    user={userData.user}
                    showStatus={this.shouldShowStatus(order)}
                />
                {this.renderRouteSings(order, userData)}
                {(extraData || orderStatusDetail) && (
                    <div className={styles.row}>
                        {extraData} {orderStatusDetail}
                    </div>
                )}
                {attendeeForm && (
                    <div className={styles.row}>
                        {attendeeForm}
                    </div>
                )}
                {this.renderAttendeeData(order)}
                {this.renderOrderLog(order)}
                {this.renderShipments(order)}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./views/order-detail/index.js